import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, Printer, Send, Edit, FileText, Link2, Copy, MessageCircle, Mail as MailIcon, RefreshCw, PenLine, CheckCircle2, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { EmailStatus } from '../components/EmailStatus';
import { QuotationSignaturePad } from '../components/QuotationSignaturePad';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Company info for documents
const COMPANY_INFO = {
  name: "NJK Logistics Pte Ltd",
  address: "212A Punggol Walk #12-741 Singapore 821212",
  phone: "98366186",
  email: "inquiry@njklogistics.com",
  paynow_uen: "202606027K"
};

const fmtSigned = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-SG', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const PUBLIC_SITE_URL = process.env.REACT_APP_PUBLIC_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '');

// Compact panel: share signing link with customer + show current signature status
const AcknowledgePanel = ({ quotation, onUpdate }) => {
  const [token, setToken] = useState(quotation.sign_token || '');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setToken(quotation.sign_token || '');
  }, [quotation.sign_token]);

  const publicUrl = token ? `${PUBLIC_SITE_URL}/sign/quotation/${token}` : '';

  const ensureToken = async () => {
    if (token) return token;
    setBusy(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/public/quotations/${quotation.id}/generate-sign-token`);
      setToken(res.data.sign_token);
      onUpdate?.();
      return res.data.sign_token;
    } catch (e) {
      toast.error('Failed to create signing link');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    const t = await ensureToken();
    if (!t) return;
    const url = `${PUBLIC_SITE_URL}/sign/quotation/${t}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Acknowledgement link copied');
    } catch {
      window.prompt('Copy this link:', url);
    }
  };

  const openWhatsApp = async () => {
    const t = await ensureToken();
    if (!t) return;
    const url = `${PUBLIC_SITE_URL}/sign/quotation/${t}`;
    const msg = encodeURIComponent(
      `*NJK Logistics — Quotation ${quotation.document_number}*\n\nDear ${quotation.customer_name || 'Customer'},\n\nKindly review and acknowledge our quotation by signing on the link below:\n${url}\n\nThank you for your business.`
    );
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  const emailCustomer = async () => {
    const t = await ensureToken();
    if (!t) return;
    const url = `${PUBLIC_SITE_URL}/sign/quotation/${t}`;
    const to = quotation.customer_email || '';
    const subject = encodeURIComponent(`Please acknowledge Quotation ${quotation.document_number}`);
    const body = encodeURIComponent(
      `Dear ${quotation.customer_name || 'Customer'},\n\nKindly review and acknowledge our quotation by signing at the link below:\n\n${url}\n\nThank you,\nNJK Logistics`
    );
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
  };

  const revoke = async () => {
    if (!window.confirm('Generate a new acknowledgement link? The old link will stop working.')) return;
    setBusy(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/public/quotations/${quotation.id}/revoke-sign-token`);
      setToken(res.data.sign_token);
      onUpdate?.();
      toast.success('New link generated. Old link is now invalid.');
    } catch {
      toast.error('Failed to regenerate link');
    } finally {
      setBusy(false);
    }
  };

  const clearSignature = async () => {
    if (!window.confirm('Clear the existing acknowledgement signature?')) return;
    setBusy(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/public/quotations/${quotation.id}/signature`);
      onUpdate?.();
      toast.success('Signature cleared');
    } catch {
      toast.error('Failed to clear signature');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t pt-6 mt-6 print:hidden" data-testid="ack-panel">
      <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
        <PenLine size={20} className="text-blue-600" /> Customer Acknowledgement
      </h3>

      {/* Inline signature pad — admin/customer signs on this device */}
      <QuotationSignaturePad quotation={quotation} onUpdate={onUpdate} />

      {/* Sharing options — only show when not yet signed */}
      {!quotation.customer_signature && (
        <div className="mt-5">
          <p className="text-sm text-gray-600 mb-3">
            <span className="font-medium">Or, send the customer a link to sign remotely:</span>
          </p>
          {publicUrl && (
            <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 mb-3 text-sm text-gray-700 break-all">
              {publicUrl}
            </div>
          )}
          <div className="flex flex-wrap gap-2 items-center">
            <Button onClick={copyLink} disabled={busy} variant="outline" data-testid="quotation-copy-link">
              <Copy size={14} className="mr-1" /> Copy link
            </Button>
            <Button onClick={openWhatsApp} disabled={busy} className="bg-[#25D366] hover:bg-[#1ebe57] text-white" data-testid="quotation-whatsapp-link">
              <MessageCircle size={14} className="mr-1" /> WhatsApp
            </Button>
            <Button onClick={emailCustomer} disabled={busy} variant="outline" data-testid="quotation-email-link">
              <MailIcon size={14} className="mr-1" /> Email to customer
            </Button>
            {token && (
              <Button onClick={revoke} disabled={busy} variant="ghost" size="sm" className="ml-auto">
                <RefreshCw size={14} className="mr-1" /> New link
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const QuotationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [converting, setConverting] = useState(false);

  const convertToDO = async () => {
    if (!window.confirm('Create a Delivery Order pre-filled from this quotation?')) return;
    setConverting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/quotations-billing/${id}/convert-to-do`);
      toast.success(`Delivery Order ${res.data.document_number} created — fill in the delivery date & address`);
      navigate(`/admin/billing/delivery-order/${res.data.delivery_order_id}/edit`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to convert quotation'));
    } finally {
      setConverting(false);
    }
  };

  useEffect(() => {
    fetchQuotation();
  }, [id]);

  const fetchQuotation = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/quotations-billing/${id}`);
      setQuotation(response.data);
    } catch (error) {
      toast.error('Failed to load quotation');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (status) => {
    setActionLoading(true);
    try {
      await axios.patch(`${BACKEND_URL}/api/quotations-billing/${id}/status`, { status });
      toast.success(`Quotation ${status}`);
      fetchQuotation();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const sendToCustomer = async () => {
    setSendingEmail(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/quotations-billing/${id}/send`);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchQuotation();
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send quotation'));
    } finally {
      setSendingEmail(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-SG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-40 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!quotation) {
    return (
      <div className="min-h-screen bg-gray-50 pt-40 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Quotation Not Found</h2>
          <Button onClick={() => navigate('/admin/billing')}>Back to Billing</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-40">
      {/* Header - Hidden in print */}
      <div className="bg-white border-b sticky top-0 z-10 print:hidden">
        <div className="container mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/admin/billing')}>
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{quotation.document_number}</h1>
                <p className="text-gray-600 mt-1">Quotation Details</p>
              </div>
            </div>
            <div className="flex gap-2">
              {quotation.status === 'draft' && (
                <>
                  <Button 
                    onClick={() => updateStatus('accepted')} 
                    className="bg-green-600 hover:bg-green-700 text-white"
                    disabled={actionLoading}
                    data-testid="accept-quotation-btn"
                  >
                    <CheckCircle size={20} className="mr-2" />
                    Accept
                  </Button>
                  <Button 
                    onClick={() => updateStatus('rejected')} 
                    variant="outline"
                    className="text-red-600 border-red-600 hover:bg-red-50"
                    disabled={actionLoading}
                    data-testid="reject-quotation-btn"
                  >
                    <XCircle size={20} className="mr-2" />
                    Reject
                  </Button>
                </>
              )}
              <Button 
                onClick={convertToDO}
                variant="outline"
                disabled={converting}
                className="text-emerald-700 border-emerald-600 hover:bg-emerald-50"
                data-testid="convert-to-do-btn"
              >
                <Truck size={20} className="mr-2" />
                {converting ? 'Converting...' : 'Convert to DO'}
              </Button>
              <Button 
                onClick={() => navigate(`/admin/quotations/${id}/edit`)}
                variant="outline"
                className="text-orange-600 border-orange-600 hover:bg-orange-50"
                data-testid="edit-quotation-btn"
              >
                <Edit size={20} className="mr-2" />
                Edit
              </Button>
              <Button 
                onClick={sendToCustomer} 
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={sendingEmail}
                data-testid="send-quotation-btn"
              >
                {sendingEmail ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send size={20} />
                    Send to Customer
                  </span>
                )}
              </Button>
              <Button onClick={() => window.open(`${BACKEND_URL}/api/quotations-billing/${id}/pdf`, '_blank')} variant="outline" data-testid="preview-pdf-btn">
                <FileText size={20} className="mr-2" />
                Preview PDF
              </Button>
              <Button onClick={handlePrint} variant="outline" data-testid="print-btn">
                <Printer size={20} className="mr-2" />
                Print
              </Button>
            </div>
          </div>
          <EmailStatus docType="quotation" docId={id} />
        </div>
      </div>

      {/* Document Content */}
      <div className="container mx-auto px-4 py-8 print:px-0 print:py-0">
        <div className="max-w-4xl mx-auto">
          <Card className="print:shadow-none print:border-none">
            <CardContent className="p-8">
              {/* Document Header */}
              <div className="flex justify-between items-start mb-8 border-b pb-6">
                <div>
                  <h2 className="text-2xl font-bold text-blue-600">{COMPANY_INFO.name}</h2>
                  <p className="text-gray-600 mt-1">{COMPANY_INFO.address}</p>
                  <p className="text-gray-600">Tel: {COMPANY_INFO.phone}</p>
                  <p className="text-gray-600">Email: {COMPANY_INFO.email}</p>
                </div>
                <div className="text-right">
                  <h1 className="text-3xl font-bold text-gray-900">QUOTATION</h1>
                  <p className="text-xl font-semibold text-blue-600 mt-2">{quotation.document_number}</p>
                  {quotation.customer_signature ? (
                    <Badge className="bg-green-100 text-green-800 mt-2" data-testid="quotation-signed-badge">
                      ✓ SIGNED
                    </Badge>
                  ) : (
                    <Badge className={
                      quotation.status === 'accepted' ? 'bg-green-100 text-green-800 mt-2' :
                      quotation.status === 'rejected' ? 'bg-red-100 text-red-800 mt-2' :
                      'bg-gray-100 text-gray-800 mt-2'
                    }>
                      {quotation.status.toUpperCase()}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Customer & Date Info */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div>
                  {/* Company Details */}
                  {quotation.company_name && (
                    <div className="mb-4">
                      <h3 className="font-semibold text-gray-700 mb-2">Company:</h3>
                      <p className="font-bold text-lg">{quotation.company_name}</p>
                      {quotation.company_email && <p className="text-gray-600">{quotation.company_email}</p>}
                      {quotation.company_contact && <p className="text-gray-600">{quotation.company_contact}</p>}
                      {quotation.company_address && <p className="text-gray-600">{quotation.company_address}</p>}
                    </div>
                  )}
                  {/* Contact Person */}
                  <h3 className="font-semibold text-gray-700 mb-2">Contact Person:</h3>
                  <p className="font-bold text-lg">{quotation.customer_name}</p>
                  <p className="text-gray-600">{quotation.customer_email}</p>
                  <p className="text-gray-600">{quotation.customer_phone}</p>
                  {(quotation.site_contact_name || quotation.site_contact_phone) && (
                    <div className="mt-3 pt-3 border-t" data-testid="qt-site-contact">
                      <h4 className="text-sm font-semibold text-gray-700">Site Contact:</h4>
                      <p className="text-gray-700">
                        {[quotation.site_contact_name, quotation.site_contact_phone].filter(Boolean).join(' — ')}
                      </p>
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="mb-2">
                    <span className="text-gray-600">Date: </span>
                    <span className="font-semibold">{formatDate(quotation.created_at)}</span>
                  </div>
                  {quotation.validity_date && (
                    <div>
                      <span className="text-gray-600">Valid Until: </span>
                      <span className="font-semibold">{formatDate(quotation.validity_date)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Line Items Table */}
              <div className="mb-8">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-3 text-left">Description</th>
                      <th className="border p-3 text-center w-16">Qty</th>
                      <th className="border p-3 text-center w-20">Hours</th>
                      <th className="border p-3 text-right w-28">Unit Price</th>
                      <th className="border p-3 text-right w-28">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotation.line_items.map((item, index) => (
                      <tr key={`line-${index}-${item.description || ''}`}>
                        <td className="border p-3">{item.description}</td>
                        <td className="border p-3 text-center">{item.quantity}</td>
                        <td className="border p-3 text-center">{item.hours ? item.hours : '-'}</td>
                        <td className="border p-3 text-right">${item.unit_price.toFixed(2)}</td>
                        <td className="border p-3 text-right">${item.amount.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-sm italic text-gray-500 mb-8" data-testid="rate-basis-note">All rates quoted are on a per-job basis.</p>

              {/* Notes */}
              {quotation.notes && (
                <div className="border-t pt-6">
                  <h3 className="font-semibold text-gray-700 mb-2">Notes:</h3>
                  <p className="text-gray-600">{quotation.notes}</p>
                </div>
              )}

              {/* Additional Surcharges */}
              <div className="border-t pt-6 mt-6">
                <h3 className="font-semibold text-gray-700 mb-3">Additional Surcharges:</h3>
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-2 text-left">No.</th>
                      <th className="border p-2 text-left">Description</th>
                      <th className="border p-2 text-center">Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(quotation.surcharges && quotation.surcharges.length > 0 
                      ? quotation.surcharges 
                      : [
                          { description: "ERP & Parking Charges", rate: "At Cost" },
                          { description: "Any Other Charges", rate: "As Per Receipt" },
                          { description: "Night 1900hrs - 0000hrs", rate: "Rate x1.5 Per Hr" },
                          { description: "Midnight 0000 Hrs - 0800 Hrs", rate: "Rate x1.5 Per Hr" },
                          { description: "Sunday & Public Holidays 24 Hrs", rate: "Rate x2 Per Hr" },
                        ]
                    ).map((surcharge, index) => (
                      <tr key={`surcharge-${index}-${surcharge.description || ''}`}>
                        <td className="border p-2">{quotation.line_items.length + index + 1}</td>
                        <td className="border p-2">{surcharge.description}</td>
                        <td className="border p-2 text-center">{surcharge.rate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Terms & Conditions */}
              <div className="border-t pt-6 mt-6">
                <h3 className="font-semibold text-gray-700 mb-2">Terms and Conditions:</h3>
                <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
                  <li>All prices quoted are subject to the prevailing 9% GST.</li>
                  <li>Subject to the availability of equipment at the time of the order.</li>
                  <li>All business transactions with NJK Logistic Pte Ltd are subject to its SLA Terms & Conditions.</li>
                  <li>Rental of the crane shall commence upon our crane's arrival at the site and cease when the crane departs from the site.</li>
                  <li>In the event of a cancellation request made less than 24 hours before the scheduled time, a fee amounting to 50% of the total booking cost will be charged.</li>
                  <li>Using of our shackles, slings, related gears and etc for lifting is at the Lessee's own risk. The Lessor will not be responsible for any consequences that may arise due to breakage or other unforeseen circumstances.</li>
                  <li>The lessee is responsible for arranging all necessary permits, documentation, and requirements for entry and work at the jobsite, and must ensure that ground preparations and the parking area are cleared prior to the arrival of our equipment.</li>
                  <li>Terms of Payment: 100% Payment Upon Confirmation</li>
                </ul>
              </div>

              {/* Payment Info with QR */}
              <div className="border-t pt-6 mt-6 bg-blue-50 -mx-8 px-8 pb-6 print:bg-white">
                <h3 className="font-semibold text-gray-700 mb-3">Payment Information:</h3>
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1">
                    <p className="text-gray-600">PayNow UEN: <span className="font-semibold">{COMPANY_INFO.paynow_uen}</span></p>
                    <p className="text-sm text-gray-500 mt-1">Scan the QR code to pay via PayNow</p>
                  </div>
                  <img src="/qr_code.jpeg" alt="PayNow QR Code" className="w-32 h-32 object-contain border border-gray-200 rounded bg-white p-1" data-testid="paynow-qr" />
                </div>
              </div>

              {/* Customer Acknowledgement Panel (admin-side controls + signature display) */}
              <AcknowledgePanel quotation={quotation} onUpdate={fetchQuotation} />

              {/* Print-only signature display */}
              <div className="border-t pt-6 mt-6 hidden print:block">
                <p className="text-sm font-semibold mb-2">Customer Acknowledgement</p>
                {quotation.customer_signature ? (
                  <img src={quotation.customer_signature} alt="Customer signature" className="h-20 object-contain border-b border-gray-400 mb-1" />
                ) : (
                  <div className="h-20 border-b border-gray-400 mb-1"></div>
                )}
                <p className="text-sm text-gray-700">Name: {quotation.customer_signature_name || ''}</p>
                <p className="text-sm text-gray-700">Date: {quotation.customer_signed_at ? new Date(quotation.customer_signed_at).toLocaleString('en-SG') : ''}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
